/*
 * Nosto product card
 *
 * Shared renderer and behaviours for Nosto-driven product cards. Both the
 * PLP/SERP grid (<nosto-search>) and the editorial product carousel build
 * their cards from the same `.js-card-product-template` markup through this
 * module, so card content, image gallery, colour swatches and the touch CTA
 * only exist once.
 *
 * Hosts create a renderer with their own options, render markup from a list of
 * Nosto products, insert it, then hydrate the inserted root:
 *
 *   const renderer = Woolman.NostoCard.create({ isFashion: true });
 *   root.innerHTML = await renderer.renderMarkup(products);
 *   renderer.hydrate(root);
 *
 * Recommendation payloads differ from Search hits (see fromRecommendation).
 */

(function () {
  const IMAGE_WIDTHS = [90, 180, 280, 390, 540, 768, 1080, 1280, 1500];
  const MAX_VISIBLE_SWATCHES = 3;
  const STOREFRONT_API_URL = '/api/2025-07/graphql.json';

  /* Shared across every renderer so the same image is never warmed twice. */
  const preloadedImageUrls = new Set();

  /* Roots whose cards need their CTA re-placed when the viewport changes.
     Disconnected roots are dropped on the next resize, so hosts that come and
     go (Nosto re-renders, section reloads) need no explicit teardown. */
  const touchCtaRoots = new Set();
  let touchCtaResizeBound = false;
  let touchCtaResizeRaf = null;

  /* Merchant and Nosto text (badges, product names, URLs) that goes into card markup. */
  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* Badge definitions from snippets/nosto-badges-data.liquid (end of <body>),
     loaded once before the first cards are drawn. Past page 1 of a PLP the
     node has no data, only the URL of the section that renders it. Cards are
     drawn by deferred scripts, so the node is there. */
  let badgesRequest = null;

  function parseBadges(node) {
    try {
      return JSON.parse(node?.textContent || '{}');
    } catch (error) {
      console.error('Invalid Nosto badge data:', error);
      return {};
    }
  }

  function loadBadges() {
    badgesRequest ??= (async () => {
      const node = document.getElementById('nosto-badges');
      const sectionUrl = node?.dataset.sectionUrl;
      if (!sectionUrl) return parseBadges(node);
      // Badges are optional: the cards wait for them, so give up after a few seconds and draw them without
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 4000);
      try {
        const response = await fetch(sectionUrl, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        return parseBadges(html.getElementById('nosto-badges'));
      } catch (error) {
        console.error('Nosto badge data failed to load:', error);
        return {};
      } finally {
        clearTimeout(timeout);
      }
    })().then((badges) => (window.badges = badges));
    return badgesRequest;
  }

  function getBadges() {
    return window.badges || {};
  }

  function sanitizeProductUrl(url) {
    if (!url || typeof url !== 'string') return '';
    const trimmed = url.trim();
    if (!trimmed) return '';

    try {
      const parsed = new URL(trimmed, window.location.origin);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
      return parsed.href;
    } catch (e) {
      return '';
    }
  }

  function withWidth(url, width) {
    if (!url) return '';
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}width=${width}`;
  }

  function isTouchOrSmallViewport() {
    if (!window.matchMedia) return false;
    const smallViewport = window.matchMedia('(max-width: 767px)').matches;
    const noHover = window.matchMedia('(hover: none)').matches;
    const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
    return smallViewport || (noHover && coarsePointer);
  }

  function placeTouchCardCta(cardBody, cta) {
    // Always last in the card body so the CTA stays a direct flex child.
    // Inserting after `.yotpo-widget-instance` is unsafe: after hydration that
    // node can be nested, which pulls Quick Shop into Yotpo's own flex row.
    if (cta.parentElement === cardBody && cardBody.lastElementChild === cta) return;
    cardBody.appendChild(cta);
  }

  /* Move every card's CTA between the media overlay (hover) and the end of the
     card body (touch), matching the current viewport. */
  function syncTouchCardCtas(root) {
    if (!root) return;

    const touchMode = isTouchOrSmallViewport();
    const cards = root.querySelectorAll('.card--product');

    cards.forEach((card) => {
      const cta = card.querySelector('[data-card-cta]');
      const cardBody = card.querySelector('.card-body');
      const mediaWrap = card.querySelector('.card-media-wrap');
      if (!cta || !cardBody || !mediaWrap) return;

      if (touchMode) {
        placeTouchCardCta(cardBody, cta);
        card.classList.add('card--touch-static-cta');
      } else {
        if (!mediaWrap.contains(cta)) {
          mediaWrap.appendChild(cta);
        }
        card.classList.remove('card--touch-static-cta');
      }
    });
  }

  function observeTouchCtaRoot(root) {
    if (!root) return;
    touchCtaRoots.add(root);

    if (touchCtaResizeBound) return;
    touchCtaResizeBound = true;

    window.addEventListener('resize', () => {
      if (touchCtaResizeRaf) window.cancelAnimationFrame(touchCtaResizeRaf);
      touchCtaResizeRaf = window.requestAnimationFrame(() => {
        touchCtaRoots.forEach((registered) => {
          if (!registered.isConnected) {
            touchCtaRoots.delete(registered);
            return;
          }
          syncTouchCardCtas(registered);
        });
      });
    });
  }

  /**
   * Normalise an image URL to a comparable key (pathname without query), so
   * variant images can be matched regardless of width/version query params.
   * @param {string} url
   * @returns {string}
   */
  function imagePathKey(url) {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (_) {
      return String(url).split('?')[0];
    }
  }

  /**
   * Extract the sibling-linking metaobject GID from a product's Nosto custom fields.
   * Nosto may expose custom fields as an array of {key, value} pairs or as a plain object.
   * @param {Array|Object} customFields
   * @returns {string|null} The metaobject GID, or null when not present.
   */
  function getSiblingLinkingGid(customFields) {
    if (!customFields) return null;
    let gid = null;
    if (Array.isArray(customFields)) {
      const siblingField = customFields.find(f => f.key === 'woolman-sibling_linking');
      gid = siblingField?.value;
    } else if (typeof customFields === 'object') {
      gid = customFields['woolman-sibling_linking'];
    }
    return gid || null;
  }

  async function storefrontQuery(query, variables) {
    const response = await fetch(STOREFRONT_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.storefrontAccessToken
      },
      body: JSON.stringify({ query, variables })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return data;
  }

  const MEDIA_IMAGES_QUERY = `
    query getMediaImages($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on MediaImage {
          id
          image {
            url
            altText
          }
        }
      }
    }
  `;

  /* Resolve `creative_force.colour_swatch` MediaImage GIDs to real image URLs. */
  async function fetchSwatchMediaImages(gids) {
    const mediaImages = {};
    if (!gids.length) return mediaImages;

    const mediaData = await storefrontQuery(MEDIA_IMAGES_QUERY, { ids: gids });
    if (mediaData.errors) return mediaImages;

    mediaData.data?.nodes?.forEach(node => {
      if (node && node.image) {
        mediaImages[node.id] = node.image.url;
      }
    });

    return mediaImages;
  }

  function collectSwatchMediaGids(records, getVariant) {
    const gids = [];
    records.forEach(record => {
      const variant = getVariant(record);
      const swatchImageGid = variant?.metafield_colour_swatch?.value;
      if (swatchImageGid && swatchImageGid.startsWith('gid://shopify/MediaImage/')) {
        gids.push(swatchImageGid);
      }
    });
    return gids;
  }

  /**
   * Renders and hydrates Nosto product cards for one host (grid or carousel).
   *
   * Options:
   *   cardType            - card style modifier class suffix
   *   cardTextAlign       - text alignment class for the card body
   *   cardImgAspectRatio  - aspect ratio class for the media figure
   *   cardImgSizingRule   - cover/contain class for the media figure
   *   isFashion           - show every product image instead of capping at 2
   *   imageSizes          - `sizes` attribute for card images
   *   eagerCount          - number (or function returning a number) of leading
   *                         cards whose image loads eagerly
   *   useCurrentOfferFallback - badge cards with the current offer PLP's
   *                         promotion when their own categories match none.
   *                         Only safe when every card belongs to that offer.
   */
  class NostoCardRenderer {
    constructor(options = {}) {
      this.options = {
        cardType: '',
        cardTextAlign: '',
        cardImgAspectRatio: '',
        cardImgSizingRule: '',
        isFashion: false,
        imageSizes: '100vw',
        eagerCount: 0,
        useCurrentOfferFallback: true,
        ...options
      };
    }

    setOptions(options = {}) {
      this.options = { ...this.options, ...options };
    }

    _eagerCount() {
      const value = this.options.eagerCount;
      return typeof value === 'function' ? (value() || 0) : (value || 0);
    }

    /**
     * Generate HTML markup for products returned from Nosto
     * @param {Array} products - Array of product data from Nosto
     * @param {Object} [options]
     * @param {number} [options.indexOffset=0] - position of the first product in the whole result set,
     *   so `data-index` stays global when a grid starts at page N or appends a page
     * @returns {Promise<string>} HTML markup for the cards
     */
    async renderMarkup(products, { indexOffset = 0 } = {}) {
      if (!products || !Array.isArray(products)) {
        console.warn('Invalid products data provided to renderMarkup');
        return '';
      }

      await loadBadges();

      // Get template content
      const template = document.querySelector('.js-card-product-template');
      if (!template) {
        console.warn('Product card template not found');
        return '';
      }

      const productsMarkup = await Promise.all(products.map(async (product, index) => {
        if (!product) return '';

        const templateContent = template.content.cloneNode(true);
        const container = document.createElement('div');
        container.appendChild(templateContent);

        try {
          // Extract product data with fallbacks
          const productHandle = product.url?.split('/').slice(-1)[0] || '';
          const productUrlRelative = productHandle ? `/products/${productHandle}` : '#';
          const productPrice = product.price ? product.price : '';
          const productPriceWithFormatting = productPrice ? Woolman.Utils.formatMoney(productPrice * 100) : '';
          const productListPrice = product.listPrice || product.list_price || '';
          const productListPriceWithFormatting = productListPrice ? Woolman.Utils.formatMoney(productListPrice * 100) : '';
          const onDiscount = productListPrice && productPrice < productListPrice;
          const productThumbUrl = product.imageUrl || product.image_url || '';
          const productAltThumbUrls = product.alternateImageUrls || product.alternate_image_urls || '';
          const productName = product.name || product.product_name || 'Product';
          const productBrand = product.brand || product.vendor || '';
          const productId = product.productId || product.product_id || '';
          const variantId = product.skus?.[0]?.id || null;
          const variantIds = product.skus ? product.skus.map(sku => sku.id) : [];
          const customFields = product.customFields || product.custom_fields || {};

          setCardProduct(container, { id: productId, handle: productHandle, variantId });
          setCardUrl(container, productUrlRelative);

          const cardType = this.options.cardType;
          const cardTextAlign = this.options.cardTextAlign;

          const cardElement = container.querySelector('.card--product');
          cardElement.setAttribute('data-index', indexOffset + index + 1);
          if (cardType) {
            cardElement.classList.add(`card--${cardType}`);
          }
          if (cardTextAlign) {
            cardElement.querySelector('.card-body').classList.add(cardTextAlign);
          }
          const productDataSpan = document.createElement('span');
          productDataSpan.className = 'hidden';
          productDataSpan.setAttribute('data-product-json', productId);
          // Text, not HTML: markup inside the payload must stay inert, and
          // readers parse this back with textContent.
          productDataSpan.textContent = JSON.stringify(product);
          cardElement.appendChild(productDataSpan);

          const productAriaLabel = window.accessibilityStrings.viewSomething.replace('{{ something }}', () => productName);
          container.querySelector('.card-media').setAttribute('aria-label', productAriaLabel);
          container.querySelector('.card-title a').setAttribute('aria-label', productAriaLabel);

          // Set primary image for normal product cards
          this.setProductImage(container, productThumbUrl, productName, index, productAltThumbUrls);

          // Set badges
          this._setProductBadges(container, customFields, product);

          // Set product title
          const titleElement = container.querySelector('[data-product-title]');
          if (titleElement) {
            titleElement.textContent = productName;
          }

          // Set vendor/brand
          const vendorElement = container.querySelector('[data-vendor]');
          if (vendorElement && productBrand) {
            vendorElement.textContent = productBrand;
          }

          // Set price
          this._setProductPrice(container, productPriceWithFormatting, productListPriceWithFormatting, onDiscount, productBrand, product);

          // Switch CTA behavior based on variant count.
          // Nosto only returns available SKUs, so a multi-size product with 1 size
          // in stock appears as skus.length === 1. To distinguish:
          // - Shopify variant names with multiple options use " / " separator
          //   (e.g. "Black / 26" = colour + size). These MUST show Quick Shop so
          //   the user sees which size they'd get.
          // - Single-option variants (e.g. "Black") or "Default Title" have no " / ".
          //   The user has no choice to make — show Add to Bag.
          const firstSkuName = (product.skus?.[0]?.name) || '';
          const hasMultipleOptionDimensions = firstSkuName.includes(' / ');
          // Whether the product would be a single-choice "Add to Bag" ignoring siblings.
          const baseSingleVariant = variantIds.length === 1 && !hasMultipleOptionDimensions;
          // Sibling-linked colour products are separate products (one SKU, no " / " in
          // the name), so the " / " heuristic alone misclassifies them as Add to Bag.
          // If sibling linking is present, optimistically show Quick Shop so the customer
          // can pick a sibling colour. The deferred swatch fetch reverts this to Add to
          // Bag if the sibling link resolves to just this product (nothing to choose).
          const hasSiblingLinking = Boolean(getSiblingLinkingGid(customFields));
          const hasSingleVariant = baseSingleVariant && !hasSiblingLinking;
          cardElement.setAttribute('data-has-single-variant', hasSingleVariant ? 'true' : 'false');
          // Preserve the sibling-independent decision so the deferred fetch can revert
          // an optimistic Quick Shop back to Add to Bag when there is no real choice.
          cardElement.setAttribute('data-single-variant-base', baseSingleVariant ? 'true' : 'false');
          const isFashionContext = Boolean(this.options.isFashion);

          const quickBuyElement = container.querySelector('quick-buy');
          if (quickBuyElement) {
            quickBuyElement.dataset.fashionContext = isFashionContext ? 'true' : 'false';
          }

          const ctaTextEls = container.querySelectorAll('[data-cta-text]');
          ctaTextEls.forEach((el) => {
            el.textContent = hasSingleVariant ? 'ADD TO BAG' : 'QUICK SHOP';
          });

          // Touch devices have no hover state: place CTA below reviews (or price) for persistent visibility.
          this._relocateCardCtaForTouchDevices(container);

          if (hasSingleVariant) {
            const directAddVariantInput = container.querySelector('product-form input[name="id"]');
            if (directAddVariantInput) {
              directAddVariantInput.value = String(variantId);
              directAddVariantInput.disabled = false;
            }
          } else {
            // Defensive: disable the direct-add form when variant data is not suitable.
            const directAddButton = container.querySelector('product-form button[name="add"]');
            if (directAddButton) {
              directAddButton.setAttribute('disabled', 'disabled');
              directAddButton.setAttribute('aria-disabled', 'true');
            }
          }

          // Store product data for deferred swatch loading
          // This prevents blocking the initial render while still allowing swatches to load later
          const swatchesContainer = container.querySelector('[data-product-swatches]');
          if (swatchesContainer) {
            swatchesContainer.setAttribute('data-product-handle', productHandle);
            swatchesContainer.setAttribute('data-custom-fields', JSON.stringify(customFields));
          }

          // Add fade-in class with staggered delay
          if (cardElement) {
            // Add a small delay based on index for staggered animation
            cardElement.style.animationDelay = `${index * 0.05}s`;
            cardElement.classList.add('fade-in');
            cardElement.setAttribute('data-track-click', 'product_card_click');
            cardElement.setAttribute('data-track-product-id', productId);
          }

          return container.innerHTML;
        } catch (error) {
          console.error('Error processing product markup:', error);
          return '';
        }
      }));

      return productsMarkup.join('');
    }

    /**
     * Wire up the behaviours that need the cards to be in the document:
     * deferred swatches, the image gallery and touch CTA placement.
     * @param {HTMLElement} root - Element containing the rendered cards
     */
    hydrate(root) {
      if (!root) return;
      this.loadDeferredSwatches(root);
      this.setupImageCarousels(root);
      syncTouchCardCtas(root);
      observeTouchCtaRoot(root);
    }

    /**
     * Load swatches for all product cards after initial render
     * Uses Intersection Observer to load swatches only when cards are visible
     * @param {HTMLElement} root - Element containing the rendered cards
     */
    loadDeferredSwatches(root) {
      const swatchContainers = root.querySelectorAll('[data-product-swatches][data-product-handle]');

      if (!swatchContainers.length) return;

      // Use Intersection Observer to load swatches only when they're near the viewport
      const observerOptions = {
        root: null,
        rootMargin: '200px', // Start loading 200px before entering viewport
        threshold: 0
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const container = entry.target;
            this._loadSingleCardSwatches(container);
            observer.unobserve(container); // Stop observing once loaded
          }
        });
      }, observerOptions);

      // Observe all swatch containers
      swatchContainers.forEach(container => observer.observe(container));
    }

    /**
     * Load swatches for a single product card
     * @param {HTMLElement} swatchContainer - The swatch container element
     */
    async _loadSingleCardSwatches(swatchContainer) {
      try {
        const productHandle = swatchContainer.getAttribute('data-product-handle');
        const customFieldsStr = swatchContainer.getAttribute('data-custom-fields');

        if (!productHandle) return;

        const customFields = customFieldsStr ? JSON.parse(customFieldsStr) : {};

        // Get the product card container for hover functionality
        const productCard = swatchContainer.closest('.card--product');
        const productData = productCard?.querySelector('[data-product-json]');
        const product = productData ? JSON.parse(productData.textContent) : null;

        if (!product || !productCard) return;

        // Call the existing swatch generation method, passing the product card as container
        await this._setProductSwatches(productCard, product, customFields, swatchContainer);

        // Clean up data attributes after loading
        swatchContainer.removeAttribute('data-product-handle');
        swatchContainer.removeAttribute('data-custom-fields');
      } catch (error) {
        console.error('Error loading deferred swatches:', error);
      }
    }

    /**
     * Render the card's primary image and record the gallery image list.
     * Also used for content slots, which have no alternate images.
     */
    setProductImage(container, thumbUrl, productName, index, alternateImageUrls) {
      const primaryImageContainer = container.querySelector('.card-media');
      if (!primaryImageContainer || !thumbUrl) return;

      const cardImgAspectRatio = this.options.cardImgAspectRatio;
      const cardImgSizingRule = this.options.cardImgSizingRule;
      const imageSizes = this.options.imageSizes;

      // Determine loading strategy based on viewport position
      const eagerCount = this._eagerCount();
      const isEager = index < eagerCount;
      const loadingStrategy = isEager ? 'eager' : 'lazy';
      const fetchPriority = isEager ? 'high' : 'low';

      // Render the primary image
      const figureElement = document.createElement('figure');
      figureElement.className = `media ${cardImgSizingRule} ${cardImgAspectRatio}`;
      const srcset = IMAGE_WIDTHS
        .map((width) => `${thumbUrl}&width=${width} ${width}w`)
        .join(', ');
      figureElement.innerHTML = `
        <img
          srcset="${escapeHtml(srcset)}"
          sizes="${imageSizes}"
          src="${escapeHtml(thumbUrl)}"
          alt="${escapeHtml(productName)}"
          loading="${loadingStrategy}"
          fetchpriority="${fetchPriority}"
          decoding="async"
          onload="this.classList.add('loaded')"
        >
      `;
      primaryImageContainer.insertBefore(figureElement, primaryImageContainer.firstChild);

      // Build the full image URL list for the carousel.
      // Fashion collections (configured per section) show all images; all others cap at 2.
      const allImages = [thumbUrl];
      if (alternateImageUrls && alternateImageUrls.length > 0) {
        const maxImages = this.options.isFashion ? (alternateImageUrls.length + 1) : 2;
        allImages.push(...alternateImageUrls.slice(0, maxImages - 1));
      }

      // Store image data on the card element so carousel setup can run after DOM insertion
      const cardElement = container.querySelector('.card--product');
      if (cardElement) {
        cardElement.dataset.imgUrls = JSON.stringify(allImages);
        cardElement.dataset.imgCount = allImages.length;
        cardElement.dataset.imgIndex = '0';
        if (allImages.length > 1) {
          cardElement.classList.add('nosto-carousel');
        }
      }
    }

    /**
     * Helper method to set product badges
     */
    _setProductBadges(container, customFields, product = {}) {
      try {
        const badgesElement = container.querySelector('[data-product-badges]');
        if (!badgesElement) return;

        let badgeMarkup = '';
        let promotionData = null;

        if (window.offerPromotionMap && Array.isArray(product.categoryIds)) {
          for (const categoryId of product.categoryIds) {
            const hit = window.offerPromotionMap[categoryId] || window.offerPromotionMap[String(categoryId)];
            if (hit) {
              promotionData = hit;
              break;
            }
          }
        }
        if (!promotionData && this.options.useCurrentOfferFallback && window.currentOfferPromotion) {
          promotionData = window.currentOfferPromotion;
        }

        if (typeof promotionData === 'string') {
          try {
            promotionData = JSON.parse(promotionData);
          } catch (e) {
            promotionData = null;
          }
        }

        const tagSvg = '<svg role="presentation" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 25 25" fill="none"><path d="M9.9157 23.6893L1.18957 14.9632C0.60378 14.3774 0.603779 13.4277 1.18957 12.8419L12.8421 1.18934C13.1234 0.908035 13.5049 0.75 13.9028 0.75H20.917C21.2416 0.75 21.5574 0.855267 21.817 1.05L23.5289 2.3339C23.9066 2.61718 24.1289 3.06176 24.1289 3.5339V10.9761C24.1289 11.374 23.9709 11.7555 23.6896 12.0368L12.037 23.6893C11.4512 24.2751 10.5015 24.2751 9.9157 23.6893Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><circle cx="16.9763" cy="7.76693" r="2.0339" stroke="currentColor" stroke-width="1.5"/></svg>';
        const giftSvg = window.giftIconSvg || '<svg role="presentation" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 12 20 22 4 22 4 12"></polyline><rect x="2" y="7" width="20" height="5"></rect><line x1="12" y1="22" x2="12" y2="7"></line><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path></svg>';
        const percentageSvg = window.percentageIconSvg || '';

        // Gift icon markup is tracked separately so it can be prepended to the
        // final badge output — the gift badge must always appear first when
        // multiple badges render on a card.
        let giftBadgeMarkup = '';
        // Track which promotion type produced an icon badge, so the matching
        // custom text badge (e.g. "Spend & Save") can be suppressed.
        let promoTypeRendered = null;

        if (promotionData && typeof promotionData === 'object') {
          const { promotion_type, icon_type, headline, background_color, text_color } = promotionData;

          let iconSvg = '';
          if (icon_type === 'tag') iconSvg = tagSvg;
          else if (icon_type === 'gift') iconSvg = giftSvg;
          else if (icon_type === 'percentage') iconSvg = percentageSvg;

          if (iconSvg) {
            const promoClass = `promo-icon-badge--${promotion_type}`;
            const styleParts = [];
            if (background_color) styleParts.push(`background-color: ${background_color}`);
            if (text_color) styleParts.push(`color: ${text_color}`);
            const styleAttr = styleParts.length ? ` style="${styleParts.join('; ')}"` : '';

            const promoBadgeHtml = `
              <div class="promo-icon-badge ${promoClass}"${styleAttr} aria-label="${headline || ''}">
                <span class="promo-icon-badge__icon">${iconSvg}</span>
              </div>
            `;
            if (icon_type === 'gift') {
              giftBadgeMarkup = promoBadgeHtml;
            } else {
              badgeMarkup += promoBadgeHtml;
            }
            promoTypeRendered = promotion_type;
          }
        }

        // Then add existing custom field badges
        let customBadges;
        if (Array.isArray(customFields)) {
          customBadges = customFields.find(f => f.key === 'woolman-custom_badges')?.value;
        } else if (typeof customFields === 'object' && customFields !== null) {
          customBadges = customFields['woolman-custom_badges'];
        }

        if (customBadges && customBadges.length > 0) {
          customBadges = typeof customBadges === 'string' ? JSON.parse(customBadges) : customBadges;
          const badges = getBadges();
          if (badges) {
            // Separate icon badges from text badges
            const iconBadges = [];
            const textBadges = [];

            customBadges.forEach(badgeName => {
              const badge = badges[badgeName];
              if (!badge) return;
              // Normalize name so "Spend & Save" / "Complimentary Gift" / "spend_save"
              // all reduce to the kebab-case promotion_type identifier.
              const normalizedName = badgeName
                .toLowerCase()
                .replace(/&/g, ' ')
                .replace(/[\s_]+/g, '-');
              // Suppress the custom text badge when the matching promo icon badge
              // has already been emitted for this promotion type (e.g. spend-save).
              if (promoTypeRendered && normalizedName === promoTypeRendered) return;
              // The Complimentary Gift badge is rendered as the gift promo-icon-badge
              // to match the offer-collection gift-with-purchase treatment. Suppress it
              // if a gift promo badge has already been emitted for this product.
              if (normalizedName === 'complimentary-gift') {
                if (giftBadgeMarkup) return;
                giftBadgeMarkup = `
                  <div class="promo-icon-badge promo-icon-badge--gift-with-purchase" aria-label="${escapeHtml(badge.label)}">
                    <span class="promo-icon-badge__icon">${giftSvg}</span>
                  </div>
                `;
                return;
              }
              if (badge.icon_type) {
                iconBadges.push({ name: badgeName, badge });
              } else {
                textBadges.push({ name: badgeName, badge });
              }
            });

            // Icon badges (after promotion badge), then text badges
            const orderedBadges = [...iconBadges, ...textBadges];

            const customBadgeMarkup = orderedBadges.map(({ name, badge }) => {
              if (badge.icon_type) {
                return `
                  <span
                    class="badge badge--icon badge--${escapeHtml(name.toLowerCase())}"
                    style="
                      --text-color: ${escapeHtml(badge.text_color)};
                      --background-color: ${escapeHtml(badge.background_color)};
                    "
                    aria-label="${escapeHtml(badge.label)}"
                  >
                    ${badge.icon_svg || ''}
                  </span>
                `;
              }
              return `
                <span
                  class="badge ${escapeHtml(name.toLowerCase())}"
                  style="
                    --text-color: ${escapeHtml(badge.text_color)};
                    --background-color: ${escapeHtml(badge.background_color)};
                  "
                >
                  ${escapeHtml(badge.label)}
                </span>
              `;
            }).join('');

            badgeMarkup += customBadgeMarkup;
          }
        }

        if (giftBadgeMarkup || badgeMarkup) {
          badgesElement.innerHTML = giftBadgeMarkup + badgeMarkup;
        }
      } catch (error) {
        console.error('Error setting product badges:', error);
      }
    }

    /**
     * Helper method to set product price
     */
    _setProductPrice(container, price, listPrice, onDiscount, productBrand, product) {
      const priceElement = container.querySelector('[data-price]');
      let salePriceHtml = '';
      let shouldUseBlackPricing = false;

      // Check if this vendor should use black pen pricing
      if (productBrand && window.blackPenVendors?.length > 0 && window.blackPenVendors.find((item) => item.vendor.toLowerCase() == productBrand.toLowerCase())) {
        shouldUseBlackPricing = true;
      }

      // Add black pen pricing class to the card if applicable
      const cardElement = container.querySelector('.card--product');
      if (cardElement && shouldUseBlackPricing && onDiscount) {
        cardElement.classList.add('black-pen-pricing');
      }

      // Check if any SKUs/variants are on sale or have lower/higher prices
      let hasVariantOnSale = false;
      let minSkuPrice = null;
      let maxSkuPrice = null;
      let showPriceRange = false;

      if (product && product.skus && Array.isArray(product.skus) && product.skus.length > 1) {
        for (const sku of product.skus) {
          const skuPrice = sku.price;
          const skuListPrice = sku.listPrice;

          // Track the minimum and maximum SKU prices
          if (skuPrice && typeof skuPrice === 'number') {
            if (minSkuPrice === null || skuPrice < minSkuPrice) {
              minSkuPrice = skuPrice;
            }
            if (maxSkuPrice === null || skuPrice > maxSkuPrice) {
              maxSkuPrice = skuPrice;
            }
          }

          // Check if this SKU is on sale (has both prices and sale price is lower)
          if (skuListPrice && skuPrice && typeof skuPrice === 'number' && typeof skuListPrice === 'number' && skuPrice < skuListPrice) {
            hasVariantOnSale = true;
          }
        }

        // Show price range if we have multiple variants with different prices
        if (minSkuPrice !== null && maxSkuPrice !== null && minSkuPrice !== maxSkuPrice) {
          showPriceRange = true;

          // If any variant is on sale, mark the entire range as on sale
          if (hasVariantOnSale) {
            onDiscount = true;
          }
        }
      }

      // Set up price display
      let priceDisplay;
      if (showPriceRange) {
        const minPriceFormatted = Woolman.Utils.formatMoney(minSkuPrice * 100);
        const maxPriceFormatted = Woolman.Utils.formatMoney(maxSkuPrice * 100);
        priceDisplay = `${minPriceFormatted} - ${maxPriceFormatted}`;
      } else {
        priceDisplay = price;
      }

      if (onDiscount) {
        priceElement.classList.add('price--on-sale');
        if (shouldUseBlackPricing) {
          priceElement.classList.add('price--black-pen');
        }
        salePriceHtml = `
      <span class="visually-hidden">Sale price</span>
      <span class="price-item price-item--sale price-item--last">${priceDisplay}</span>
      `;
      }

      if (priceElement) {
        priceElement.innerHTML = `
            <div class="flex">
              <div class="price__regular">
                <span class="visually-hidden">Regular price</span>
                <span class="price-item price-item--regular">${priceDisplay}</span>
              </div>
              ${listPrice && !showPriceRange ? `
                <div class="price__sale">
                  <span class="visually-hidden">Regular price</span>
                  <span>
                    <s class="price-item price-item--regular style--subdued">${listPrice}</s>
                    ${salePriceHtml}
                  </span>

                </div>
              ` : showPriceRange && onDiscount ? `
                <div class="price__sale">
                  <span class="visually-hidden">Sale price</span>
                  <span class="price-item--sale">
                    ${salePriceHtml}
                  </span>
                </div>
              ` : ''}
            </div>
          `;
      }
    }

    /**
     * Force a card into Quick Shop mode (used when siblings are discovered after render).
     * @param {HTMLElement} container - The card element or a wrapper containing it.
     */
    _ensureQuickShopCta(container) {
      if (!container) return;
      const card = container.matches?.('.card--product')
        ? container
        : container.querySelector('.card--product');
      if (!card || card.getAttribute('data-has-single-variant') !== 'true') return;

      card.setAttribute('data-has-single-variant', 'false');
      card.querySelectorAll('[data-cta-text]').forEach((el) => {
        el.textContent = 'QUICK SHOP';
      });
    }

    /**
     * Revert an optimistic Quick Shop card back to Add to Bag when a sibling link
     * turns out to resolve to just this product (no colour/option choice to make).
     * Only affects cards that would be single-variant without the sibling link.
     * @param {HTMLElement} container - The card element or a wrapper containing it.
     * @param {Object} product - The product data (for the direct-add variant id).
     */
    _revertToAddToBagIfBase(container, product) {
      if (!container) return;
      const card = container.matches?.('.card--product')
        ? container
        : container.querySelector('.card--product');
      if (!card) return;
      // Products with a genuine size/option choice must stay Quick Shop.
      if (card.getAttribute('data-single-variant-base') !== 'true') return;
      if (card.getAttribute('data-has-single-variant') === 'true') return;

      card.setAttribute('data-has-single-variant', 'true');
      card.querySelectorAll('[data-cta-text]').forEach((el) => {
        el.textContent = 'ADD TO BAG';
      });

      const variantId = product?.skus?.[0]?.id;
      const directAddVariantInput = card.querySelector('product-form input[name="id"]');
      if (directAddVariantInput && variantId) {
        directAddVariantInput.value = String(variantId);
        directAddVariantInput.disabled = false;
      }
      const directAddButton = card.querySelector('product-form button[name="add"]');
      if (directAddButton) {
        directAddButton.removeAttribute('disabled');
        directAddButton.removeAttribute('aria-disabled');
      }
    }

    /**
     * Set product swatches from sibling products or colour variants
     * @param {HTMLElement} container - The card element or a wrapper containing it
     * @param {Object} product - The product data
     * @param {Object} customFields - Custom fields from the product
     * @param {HTMLElement} targetSwatchContainer - Optional specific swatch container to update
     */
    async _setProductSwatches(container, product, customFields, targetSwatchContainer = null) {
      try {
        const swatchesContainer = targetSwatchContainer || container.querySelector('[data-product-swatches]');
        if (!swatchesContainer) return;

        // First, check for sibling products GID from custom fields
        const siblingProductsGid = getSiblingLinkingGid(customFields);

        // If sibling products exist, use them
        if (siblingProductsGid) {
          const siblingProducts = await this._fetchSiblingProducts(siblingProductsGid);
          if (siblingProducts && siblingProducts.length > 1) {
            const swatchesMarkup = await this._generateSwatchesMarkup(siblingProducts, product, 'sibling');
            swatchesContainer.innerHTML = swatchesMarkup;
            // Attach hover listeners after rendering
            this._attachSwatchHoverListeners(swatchesContainer, container);
            // Defensive: siblings mean the customer has a colour choice, so ensure the
            // card shows Quick Shop even if the CTA heuristic classified it otherwise.
            this._ensureQuickShopCta(container);
            return;
          }
        }

        // Otherwise, check for color variants within the same product
        const productHandle = product.url?.split('/').slice(-1)[0] || '';
        if (productHandle) {
          const variants = await this._fetchProductVariants(productHandle);
          if (variants && variants.length > 1) {
            const swatchesMarkup = await this._generateSwatchesMarkup(variants, product, 'variant');
            swatchesContainer.innerHTML = swatchesMarkup;
            // Attach hover listeners after rendering
            this._attachSwatchHoverListeners(swatchesContainer, container);
            return;
          }
        }

        // No real choice: no >1 siblings and no >1 colour variants. If this card was
        // optimistically forced to Quick Shop by a self-referential sibling link,
        // revert it to Add to Bag.
        if (siblingProductsGid) {
          this._revertToAddToBagIfBase(container, product);
        }
      } catch (error) {
        console.error('Error setting product swatches:', error);
      }
    }

    /**
     * Fetch sibling products from Shopify metaobject
     */
    async _fetchSiblingProducts(gid) {
      try {
        if (!window.storefrontAccessToken) {
          console.warn('Storefront access token not configured - cannot fetch sibling products');
          return [];
        }

        // First, get the metaobject to retrieve the product GIDs
        const metaobjectQuery = `
          query getSiblingProductGids($id: ID!) {
            metaobject(id: $id) {
              fields {
                key
                value
              }
            }
          }
        `;

        const metaobjectData = await storefrontQuery(metaobjectQuery, { id: gid });

        if (metaobjectData.errors) {
          console.error('GraphQL errors:', metaobjectData.errors);
          return [];
        }

        // Find the linked_products field and extract the product GIDs
        const linkedProductsField = metaobjectData.data?.metaobject?.fields?.find(f =>
          f.key === 'linked_products' || f.key === 'products'
        );

        if (!linkedProductsField || !linkedProductsField.value) {
          console.warn('No linked_products field found in metaobject');
          return [];
        }

        // Parse the product GIDs (they should be in the value as a JSON array or string)
        let productGids;
        try {
          productGids = typeof linkedProductsField.value === 'string'
            ? JSON.parse(linkedProductsField.value)
            : linkedProductsField.value;
        } catch (e) {
          console.error('Error parsing linked_products value:', e);
          return [];
        }

        if (!Array.isArray(productGids) || productGids.length === 0) {
          console.warn('No product GIDs found in linked_products field');
          return [];
        }

        // Now fetch the actual products using their GIDs
        const productsQuery = `
          query getSiblingProducts($ids: [ID!]!) {
            nodes(ids: $ids) {
              ... on Product {
                id
                handle
                title
                availableForSale
                featuredImage {
                  url
                  altText
                }
                options {
                  name
                  values
                }
                variants(first: 1) {
                  nodes {
                    id
                    barcode
                    metafield_colour_swatch: metafield(namespace: "creative_force", key: "colour_swatch") {
                      value
                    }
                    metafield_item_number: metafield(namespace: "d365", key: "item_number") {
                      value
                    }
                    metafield_fenwick_colour: metafield(namespace: "d365", key: "fenwick_colour") {
                      value
                    }
                  }
                }
              }
            }
          }
        `;

        const productsData = await storefrontQuery(productsQuery, { ids: productGids });

        if (productsData.errors) {
          console.error('GraphQL errors:', productsData.errors);
          return [];
        }

        const products = productsData.data?.nodes?.filter(node => node !== null) || [];

        // Resolve swatch MediaImage GIDs to real image URLs
        const mediaImageGids = collectSwatchMediaGids(products, (product) => product.variants?.nodes?.[0]);
        const mediaImages = await fetchSwatchMediaImages(mediaImageGids);

        // Add the actual image URLs to the products
        products.forEach(product => {
          const variant = product.variants?.nodes?.[0];
          if (variant?.metafield_colour_swatch?.value) {
            const mediaImageGid = variant.metafield_colour_swatch.value;
            if (mediaImages[mediaImageGid]) {
              variant.metafield_colour_swatch.imageUrl = mediaImages[mediaImageGid];
            }
          }
        });

        return products;
      } catch (error) {
        // Silently handle aborted requests (user navigated away)
        if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
          return [];
        }
        // Log other errors
        console.error('Error fetching sibling products:', error);
        return [];
      }
    }

    /**
     * Fetch product variants with color option from Shopify Storefront API
     */
    async _fetchProductVariants(productHandle) {
      try {
        if (!window.storefrontAccessToken) {
          console.warn('Storefront access token not configured - cannot fetch product variants');
          return [];
        }

        // Query to get all variants of a product with color option
        const variantsQuery = `
          query getProductVariants($handle: String!) {
            product(handle: $handle) {
              id
              handle
              title
              availableForSale
              options {
                name
                values
              }
              variants(first: 100) {
                nodes {
                  id
                  title
                  availableForSale
                  barcode
                  image {
                    url
                    altText
                  }
                  selectedOptions {
                    name
                    value
                  }
                  metafield_colour_swatch: metafield(namespace: "creative_force", key: "colour_swatch") {
                    value
                  }
                  metafield_item_number: metafield(namespace: "d365", key: "item_number") {
                    value
                  }
                  metafield_fenwick_colour: metafield(namespace: "d365", key: "fenwick_colour") {
                    value
                  }
                }
              }
            }
          }
        `;

        const variantsData = await storefrontQuery(variantsQuery, { handle: productHandle });

        if (variantsData.errors) {
          console.error('GraphQL errors:', variantsData.errors);
          return [];
        }

        const product = variantsData.data?.product;
        if (!product) {
          console.warn('Product not found');
          return [];
        }

        // Find the color option (could be named Colour, Color, etc.)
        const colorOption = product.options?.find(opt =>
          opt.name.toLowerCase().includes('colour') || opt.name.toLowerCase().includes('color')
        );

        if (!colorOption) {
          // No color option, return empty array
          return [];
        }

        const variants = product.variants?.nodes || [];

        // Filter variants to only include unique color values
        const seenColors = new Set();
        const uniqueColorVariants = variants.filter(variant => {
          const colorValue = variant.selectedOptions?.find(opt =>
            opt.name.toLowerCase().includes('colour') || opt.name.toLowerCase().includes('color')
          )?.value;

          if (!colorValue || seenColors.has(colorValue.toLowerCase())) {
            return false;
          }
          seenColors.add(colorValue.toLowerCase());
          return true;
        });

        // Resolve swatch MediaImage GIDs to real image URLs
        const mediaImageGids = collectSwatchMediaGids(uniqueColorVariants, (variant) => variant);
        const mediaImages = await fetchSwatchMediaImages(mediaImageGids);

        // Add the actual image URLs to the variants and transform to match sibling product structure
        const transformedVariants = uniqueColorVariants.map(variant => {
          if (variant?.metafield_colour_swatch?.value) {
            const mediaImageGid = variant.metafield_colour_swatch.value;
            if (mediaImages[mediaImageGid]) {
              variant.metafield_colour_swatch.imageUrl = mediaImages[mediaImageGid];
            }
          }

          // Transform variant to match sibling product structure
          return {
            id: variant.id,
            handle: product.handle,
            title: variant.title,
            availableForSale: variant.availableForSale,
            isVariant: true, // Flag to distinguish from sibling products
            variantId: variant.id.split('/').pop(), // Extract numeric ID
            selectedOptions: variant.selectedOptions,
            metafield_colour_swatch: variant.metafield_colour_swatch,
            metafield_item_number: variant.metafield_item_number,
            metafield_fenwick_colour: variant.metafield_fenwick_colour,
            barcode: variant.barcode,
            image: variant.image // Include variant image
          };
        });

        return transformedVariants;
      } catch (error) {
        // Silently handle aborted requests (user navigated away)
        if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
          return [];
        }
        // Log other errors
        console.error('Error fetching product variants:', error);
        return [];
      }
    }

    /**
     * Determine which variant a variant-product card is currently showing so only
     * that swatch is highlighted on load. Prefers the variant whose image matches
     * the card's displayed image, then the product's default sku, then the first
     * available variant.
     * @param {Array} variants
     * @param {Object} product - The Nosto product object
     * @returns {(string|number|null)} The current variant id, or null.
     */
    _resolveCurrentVariantId(variants, product) {
      const available = (variants || []).filter(v => v.availableForSale);
      if (!available.length) return null;

      const currentImageKey = imagePathKey(product?.imageUrl || product?.image_url);
      if (currentImageKey) {
        const byImage = available.find(v => imagePathKey(v.image?.url) === currentImageKey);
        if (byImage) return byImage.variantId;
      }

      const defaultSkuId = product?.skus?.[0]?.id;
      if (defaultSkuId != null) {
        const bySku = available.find(v => String(v.variantId) === String(defaultSkuId));
        if (bySku) return bySku.variantId;
      }

      return available[0].variantId;
    }

    /**
     * Generate swatches markup for sibling products or product variants
     * @param {Array} items - Array of sibling products or variants
     * @param {Object} currentProduct - Current Nosto product object
     * @param {String} type - 'sibling' or 'variant'
     */
    async _generateSwatchesMarkup(items, currentProduct, type = 'sibling') {
      let totalEligibleSwatches = 0;

      // For variant products every swatch shares the same product handle, so we
      // can't use the handle to find the active colour. Resolve the single current
      // variant up-front (matched to the image the card is showing) instead.
      const currentVariantId = type === 'variant'
        ? this._resolveCurrentVariantId(items, currentProduct)
        : null;

      const swatches = items.map(item => {
        let colorValue, isCurrentItem, isAvailable, swatchImage, ean, itemNumber, fenwickColour;

        if (type === 'variant') {
          // Handle regular product variants
          colorValue = item.selectedOptions?.find(opt =>
            opt.name.toLowerCase().includes('colour') || opt.name.toLowerCase().includes('color')
          )?.value;

          if (!colorValue) return null;

          isCurrentItem = currentVariantId != null && String(item.variantId) === String(currentVariantId);
          isAvailable = item.availableForSale;

          // Skip sold-out variants
          if (!isAvailable) return null;
          totalEligibleSwatches++;

          // Get swatch data from variant metafields
          swatchImage = item.metafield_colour_swatch?.imageUrl || null;
          ean = item.barcode;
          itemNumber = item.metafield_item_number?.value;
          fenwickColour = item.metafield_fenwick_colour?.value;

          // Generate swatch HTML for variant
          return this._generateSingleSwatchMarkup({
            item,
            colorValue,
            swatchImage,
            ean,
            itemNumber,
            fenwickColour,
            isCurrentItem,
            isAvailable,
            type: 'variant'
          });
        } else {
          // Handle sibling products (original logic)
          const colorOption = item.options?.find(opt =>
            opt.name.toLowerCase().includes('colour') || opt.name.toLowerCase().includes('color')
          );

          if (!colorOption || !colorOption.values.length) return null;

          colorValue = colorOption.values[0];
          const variant = item.variants?.nodes?.[0];
          // Derive the handle from the Nosto product URL (the product object doesn't
          // reliably expose `.handle`), so the current colour is highlighted on load.
          const currentSiblingHandle = currentProduct.url?.split('/').slice(-1)[0] || currentProduct.handle;
          isCurrentItem = item.handle === currentSiblingHandle;
          isAvailable = item.availableForSale;

          // Skip sold-out products
          if (!isAvailable) return null;
          totalEligibleSwatches++;

          // Get swatch data from variant metafields
          swatchImage = variant?.metafield_colour_swatch?.imageUrl || null;
          ean = variant?.barcode;
          itemNumber = variant?.metafield_item_number?.value;
          fenwickColour = variant?.metafield_fenwick_colour?.value;

          // Generate swatch HTML for sibling product
          return this._generateSingleSwatchMarkup({
            item,
            colorValue,
            swatchImage,
            ean,
            itemNumber,
            fenwickColour,
            isCurrentItem,
            isAvailable,
            type: 'sibling'
          });
        }
      }).filter(Boolean);

      if (swatches.length > 1) {
        const visibleSwatches = swatches.slice(0, MAX_VISIBLE_SWATCHES);
        const remainingCount = Math.max(0, totalEligibleSwatches - MAX_VISIBLE_SWATCHES);
        const moreIndicator = remainingCount > 0
          ? `<span class="swatches-more" aria-label="${remainingCount} more colours">+${remainingCount}</span>`
          : '';

        return `
          <div class="nosto-swatches-container fade-in">
            <span class="visually-hidden">Available in</span>
            <div class="swatches swatches--mobile flex" style="gap: 8px;">
              ${visibleSwatches.join('')}
              ${moreIndicator}
            </div>
            <div class="swatches swatches--desktop flex" style="gap: 8px;">
              ${visibleSwatches.join('')}
              ${moreIndicator}
            </div>
          </div>
        `;
      }

      return '';
    }

    /**
     * Generate single swatch markup
     * @param {Object} params - Parameters object
     * @param {Object} params.item - Product or variant object
     * @param {String} params.colorValue - Color value to display
     * @param {String} params.swatchImage - Swatch image URL
     * @param {String} params.ean - EAN barcode
     * @param {String} params.itemNumber - Item number
     * @param {String} params.fenwickColour - Fenwick colour code
     * @param {Boolean} params.isCurrentItem - Whether this is the current item
     * @param {Boolean} params.isAvailable - Whether the item is available
     * @param {String} params.type - 'sibling' or 'variant'
     */
    _generateSingleSwatchMarkup({ item, colorValue, swatchImage, ean, itemNumber, fenwickColour, isCurrentItem, isAvailable, type = 'sibling' }) {
      let productUrl;

      if (type === 'variant') {
        // For variants, link to the same product with variant query parameter
        productUrl = `/products/${item.handle}?variant=${item.variantId}`;
      } else {
        // For sibling products, link to the different product
        productUrl = `/products/${item.handle}`;
      }

      const linkClass = `swatch ${isCurrentItem ? 'current-product' : ''} ${!isAvailable ? 'sold-out' : ''}`;

      // Get the image data for hover functionality
      let imageUrl = '';
      let imageAlt = '';

      if (type === 'variant') {
        // For variants, use the variant image
        imageUrl = item.image?.url || '';
        imageAlt = item.image?.altText || colorValue;
      } else {
        // For sibling products, use the featured image
        imageUrl = item.featuredImage?.url || '';
        imageAlt = item.featuredImage?.altText || item.title || colorValue;
      }

      // Determine swatch display method (following your existing logic)
      let swatchContent = '';

      if (swatchImage) {
        swatchContent = `
          <img
            src="${swatchImage}?width=110&height=110"
            alt="${colorValue}"
            title="${colorValue}"
            class="color"
            width="34"
            height="34"
            loading="lazy"
            fetchpriority="low"
            data-metafield-swatch
          >
        `;
      } else {
        // No swatch image available - skip this swatch
        return null;
      }

      // Extract numeric product ID for modal targeting
      let productId = '';
      if (type === 'sibling' && item.id) {
        // GID format: gid://shopify/Product/12345678
        const idMatch = item.id.match(/(\d+)$/);
        productId = idMatch ? idMatch[1] : '';
      }

      // Add data attributes for click image preview and link update
      const imageDataAttrs = imageUrl ? `data-hover-image="${imageUrl}" data-hover-image-alt="${imageAlt}"` : '';
      const productDataAttrs = `data-product-url="${productUrl}" data-product-handle="${item.handle}"${productId ? ` data-product-id="${productId}"` : ''}`;

      const linkElement = isAvailable ?
        `<a href="${productUrl}" class="${linkClass}" ${imageDataAttrs} ${productDataAttrs} tabindex="-1">` :
        `<div class="${linkClass}" ${imageDataAttrs} ${productDataAttrs}>`;

      const closingElement = isAvailable ? '</a>' : '</div>';

      return `
        ${linkElement}
          ${swatchContent}
          <span class="visually-hidden">${colorValue}</span>
        ${closingElement}
      `;
    }

    /**
     * Attach click event listeners to swatches for image preview and link update.
     * Clicking a swatch keeps the user on the listing, updates the card image to
     * the selected colour, and re-points all card links (including CTA) to the
     * chosen variant/sibling PDP.
     * @param {HTMLElement} swatchesContainer - The container with the swatches
     * @param {HTMLElement} productCard - The product card containing the image
     */
    _attachSwatchHoverListeners(swatchesContainer, productCard) {
      const swatches = swatchesContainer.querySelectorAll('.swatch[data-hover-image]');
      if (!swatches.length) return;

      // Find the primary image element in the product card
      const cardMedia = productCard.querySelector('.card-media');
      if (!cardMedia) return;

      const primaryImage = cardMedia.querySelector('img');
      if (!primaryImage) return;

      // Add click listener to each swatch
      swatches.forEach(swatch => {
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const imageUrl = swatch.getAttribute('data-hover-image');
          const imageAlt = swatch.getAttribute('data-hover-image-alt') || '';
          const rawProductUrl = swatch.getAttribute('data-product-url') || '';
          const productUrl = sanitizeProductUrl(rawProductUrl);
          const productHandle = swatch.getAttribute('data-product-handle') || '';

          if (!imageUrl) return;

          let safeImageUrl;
          try {
            const parsedUrl = new URL(imageUrl, window.location.origin);
            if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') return;
            safeImageUrl = parsedUrl.toString();
          } catch (_) {
            return;
          }

          // --- Update card image ---
          const srcset = IMAGE_WIDTHS
            .map((width) => `${safeImageUrl}&width=${width} ${width}w`)
            .join(', ');
          primaryImage.setAttribute('srcset', srcset);
          primaryImage.setAttribute('src', `${safeImageUrl}&width=540`);
          primaryImage.setAttribute('alt', imageAlt);

          // --- Update the carousel image array so hover/leave doesn't show old images ---
          const card = swatch.closest('.card--product');
          if (card) {
            // Immediately set the swatch image as the only carousel image to prevent
            // glitches while we fetch the full image set asynchronously.
            const newUrls = [safeImageUrl];
            card._imgUrls = newUrls;
            card.dataset.imgUrls = JSON.stringify(newUrls);
            card.dataset.imgIndex = '0';
            card._carouselPreloaded = false;

            // Both swatch types resolve their carousel from the product's image
            // list, but differently:
            //  - Siblings are separate products, so /products/{handle}.js returns
            //    that colour's own images — restore the first N as before.
            //  - Normal colour variants share one product whose images cover every
            //    colour. Find the selected variant image in that list and assume the
            //    image immediately after it is the matching hover shot, so hover/leave
            //    stay on the selected colour.
            const isSiblingSwatch = Boolean(swatch.getAttribute('data-product-id'));

            if (productHandle) {
              fetch(`/products/${productHandle}.js`)
                .then(res => res.json())
                .then(productData => {
                  if (!productData || !Array.isArray(productData.images) || productData.images.length === 0) return;

                  // Strip query params so URLs compare consistently and so the
                  // carousel can re-append its own &width= sizing.
                  const fullUrls = productData.images.map(img => img.replace(/\?.*$/, ''));

                  let newImgUrls;

                  if (isSiblingSwatch) {
                    // Apply the same image cap as initial card rendering:
                    // fashion collections show all images, others cap at 2.
                    const isFashion = Boolean(this.options.isFashion);
                    newImgUrls = (!isFashion && fullUrls.length > 2) ? fullUrls.slice(0, 2) : fullUrls;
                  } else {
                    // Locate the selected variant image (compare by path, ignoring
                    // query/size) and pair it with the next image as the hover shot.
                    const selectedPath = imagePathKey(safeImageUrl);
                    const selectedIndex = fullUrls.findIndex(url => imagePathKey(url) === selectedPath);

                    if (selectedIndex === -1) {
                      // Couldn't locate it — keep just the selected colour image.
                      newImgUrls = [safeImageUrl.replace(/\?.*$/, '')];
                    } else {
                      newImgUrls = [fullUrls[selectedIndex]];
                      if (selectedIndex + 1 < fullUrls.length) {
                        newImgUrls.push(fullUrls[selectedIndex + 1]);
                      }
                    }
                  }

                  card._imgUrls = newImgUrls;
                  card.dataset.imgUrls = JSON.stringify(newImgUrls);
                  card.dataset.imgCount = String(newImgUrls.length);
                  card.dataset.imgIndex = '0';

                  // Proactively warm the non-primary images (esp. the hover shot)
                  // now, using the card's real srcset/sizes, so the first hover
                  // after a swatch click is instant instead of waiting on a fetch.
                  for (let i = 1; i < newImgUrls.length; i++) {
                    this._preloadCarouselImage(card, newImgUrls[i]);
                  }
                  card._carouselPreloaded = true;

                  // Rebuild indicator segments to match new image count
                  const indicator = card.querySelector('[data-img-indicator]');
                  if (indicator) {
                    indicator.innerHTML = newImgUrls
                      .map((_, i) => `<span class="card-img-indicator-segment${i === 0 ? ' active' : ''}" data-img-segment="${i}"></span>`)
                      .join('');
                  }
                })
                .catch(() => { /* Silently keep single-image fallback */ });
            }
          }

          // --- Update all card links to the selected product/variant URL ---
          if (productUrl && card) {
            // Update the card media link
            const mediaLink = card.querySelector('.card-media');
            if (mediaLink && mediaLink.tagName === 'A') {
              mediaLink.setAttribute('href', productUrl);
            }

            // Update the card title link
            const titleLink = card.querySelector('.card-title a');
            if (titleLink) {
              titleLink.setAttribute('href', productUrl);
            }

            // Update quick-buy element so it opens the correct modal
            const quickBuyEl = card.querySelector('quick-buy');
            if (quickBuyEl && productHandle) {
              quickBuyEl.setAttribute('data-url', productUrl);

              // Update the instance properties that quick-buy caches in constructor
              quickBuyEl.quickBuyProductHandle = productHandle;
              quickBuyEl.processingModal = false;

              // If we have a product ID (sibling), update the target modal ID.
              // Take the id from the match rather than the attribute, so the
              // value reaching the href provably contains nothing but the
              // characters below.
              const productIdMatch = /^[A-Za-z0-9_-]+$/.exec(swatch.getAttribute('data-product-id') || '');
              if (productIdMatch) {
                const swatchProductId = productIdMatch[0];
                const newModalId = `modal-${swatchProductId}-product`;
                quickBuyEl.dataset.product = swatchProductId;
                quickBuyEl.targetModalId = newModalId;
                const quickBuyLink = quickBuyEl.querySelector('a[data-product-handle]');
                if (quickBuyLink) {
                  quickBuyLink.setAttribute('data-product-handle', productHandle);
                  quickBuyLink.setAttribute('href', `#${newModalId}`);
                }
              } else {
                const quickBuyLink = quickBuyEl.querySelector('a[data-product-handle]');
                if (quickBuyLink) {
                  quickBuyLink.setAttribute('data-product-handle', productHandle);
                }
              }

              // Remove any previously cached modal so it fetches fresh
              document.querySelectorAll('[data-quick-buy-modal]').forEach((m) => m.remove());
            }
          }

          // --- Mark clicked swatch as active ---
          // Compare against the raw attribute value (relative path). `productUrl`
          // is sanitized to an absolute href and would never match the relative
          // data-product-url attribute, so the active class was never applied.
          if (card) {
            const activeUrl = swatch.getAttribute('data-product-url');
            // Toggling covers the matching swatch in both the mobile & desktop
            // containers (they share the same data-product-url).
            card.querySelectorAll('.nosto-product-swatches .swatch').forEach(s => {
              s.classList.toggle('current-product', s.getAttribute('data-product-url') === activeUrl);
            });
          }
        });
      });
    }

    _relocateCardCtaForTouchDevices(container) {
      if (!isTouchOrSmallViewport()) return;

      const card = container.querySelector('.card--product');
      const cta = container.querySelector('[data-card-cta]');
      const cardBody = container.querySelector('.card-body');
      if (!card || !cta || !cardBody) return;

      placeTouchCardCta(cardBody, cta);
      card.classList.add('card--touch-static-cta');
    }

    _preloadImage(url) {
      if (!url || preloadedImageUrls.has(url)) return;
      preloadedImageUrls.add(url);
      const img = new Image();
      img.decoding = 'async';
      img.src = withWidth(url, 540);
    }

    /**
     * Warm a carousel image using the same srcset/sizes the card renders with, so
     * the browser fetches (and caches) the exact responsive candidate it will
     * display. Used after a swatch click so hovering to the next image is instant
     * rather than triggering a fresh network request.
     * @param {HTMLElement} card - The .card--product element
     * @param {string} url - Base image URL (no width query)
     */
    _preloadCarouselImage(card, url) {
      if (!url || preloadedImageUrls.has(url)) return;
      preloadedImageUrls.add(url);

      const cardImg = card.querySelector('.card-media img');
      const img = new Image();
      img.decoding = 'async';
      img.sizes = cardImg?.getAttribute('sizes') || this.options.imageSizes;
      img.srcset = IMAGE_WIDTHS
        .map((w) => `${withWidth(url, w)} ${w}w`)
        .join(', ');
      img.src = withWidth(url, 540);
    }

    /**
     * Return the cached image URL array for a card, parsing once from the
     * data attribute and storing the result as a JS property for future access.
     */
    _getCardImageUrls(card) {
      if (!card._imgUrls) {
        card._imgUrls = JSON.parse(card.dataset.imgUrls || '[]');
      }
      return card._imgUrls;
    }

    /**
     * Ensure all carousel images for a card are preloaded.
     * Safe to call multiple times — only fires actual preloads once.
     */
    _ensureCarouselPreloaded(card) {
      if (card._carouselPreloaded) return;
      card._carouselPreloaded = true;
      const imgUrls = this._getCardImageUrls(card);
      for (let i = 1; i < imgUrls.length; i++) {
        this._preloadImage(imgUrls[i]);
      }
    }

    /**
     * Update the displayed image on a card to the given index.
     * Updates the img src/srcset and the indicator active state.
     * @param {HTMLElement} card - The .card--product element
     * @param {number} index - The image index to show
     */
    _setCardImageIndex(card, index) {
      const imgUrls = this._getCardImageUrls(card);
      if (!imgUrls.length || index < 0 || index >= imgUrls.length) return;

      // Preload all carousel images on first navigation (covers hover, click, and touch)
      this._ensureCarouselPreloaded(card);

      card.dataset.imgIndex = index;
      const img = card.querySelector('.card-media img');
      if (img) {
        const url = imgUrls[index];
        // `painted` stays false until this src has actually decoded. A tap used
        // to swap the src and navigate before that, and the back/forward cache
        // then restored the unloaded second image.
        img.dataset.painted = 'false';
        if (!img._paintListener) {
          img._paintListener = () => {
            img.dataset.painted = 'true';
          };
          img.addEventListener('load', img._paintListener);
        }
        img.loading = 'eager';
        img.srcset = IMAGE_WIDTHS
          .map((width) => `${withWidth(url, width)} ${width}w`)
          .join(', ');
        img.src = withWidth(url, 540);
      }

      // Update indicator segments
      card.querySelectorAll('[data-img-segment]').forEach((seg, i) => {
        seg.classList.toggle('active', i === index);
      });
    }

    /**
     * Build indicator segments and attach carousel event listeners for all product
     * cards under `root`. Call this after products are inserted into the DOM.
     * @param {HTMLElement} root - Element containing the rendered cards
     */
    setupImageCarousels(root) {
      const cards = root.querySelectorAll('.card--product[data-img-urls]');
      cards.forEach(card => {
        // Skip if already initialised
        if (card.dataset.carouselReady) return;
        card.dataset.carouselReady = 'true';

        const imgUrls = JSON.parse(card.dataset.imgUrls || '[]');
        const imgCount = imgUrls.length;

        // Build indicator segments
        const indicator = card.querySelector('[data-img-indicator]');
        if (indicator) {
          indicator.innerHTML = imgUrls
            .map((_, i) => `<span class="card-img-indicator-segment${i === 0 ? ' active' : ''}" data-img-segment="${i}"></span>`)
            .join('');
        }

        // Nothing more to do for single-image cards
        if (imgCount <= 1) return;

        const prevBtn = card.querySelector('[data-img-prev]');
        const nextBtn = card.querySelector('[data-img-next]');
        const imageContainer = card.querySelector('.card-media-wrap');

        // Secondary image on real hover only. iOS fires mouseenter, and
        // sometimes a pointerenter typed as mouse, for a tap. That swap lands
        // just before navigation and the back/forward cache restores the
        // second image before it has loaded.
        if (imageContainer) {
          let lastTouchAt = 0;
          imageContainer.addEventListener('touchstart', () => {
            lastTouchAt = Date.now();
          }, { passive: true });

          const isMouseHover = (event) => {
            if (event.pointerType !== 'mouse') return false;
            return Date.now() - lastTouchAt > 700;
          };

          imageContainer.addEventListener('pointerenter', (event) => {
            if (!isMouseHover(event)) return;
            const currentCount = this._getCardImageUrls(card).length;
            if (currentCount > 1 && parseInt(card.dataset.imgIndex || '0', 10) === 0) {
              this._setCardImageIndex(card, 1);
            }
          });
          imageContainer.addEventListener('pointerleave', (event) => {
            if (!isMouseHover(event)) return;
            this._setCardImageIndex(card, 0);
          });
        }

        // Arrow navigation — read count dynamically so swatch changes are respected
        if (prevBtn) {
          prevBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const currentUrls = this._getCardImageUrls(card);
            const count = currentUrls.length;
            if (count <= 1) return;
            const current = parseInt(card.dataset.imgIndex || '0');
            const targetIndex = (current - 1 + count) % count;
            this._setCardImageIndex(card, targetIndex);
          });
        }

        if (nextBtn) {
          nextBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const currentUrls = this._getCardImageUrls(card);
            const count = currentUrls.length;
            if (count <= 1) return;
            const current = parseInt(card.dataset.imgIndex || '0');
            const targetIndex = (current + 1) % count;
            this._setCardImageIndex(card, targetIndex);
          });
        }

        // Indicator segment click
        if (indicator) {
          indicator.addEventListener('click', (e) => {
            const seg = e.target.closest('[data-img-segment]');
            if (seg) this._setCardImageIndex(card, parseInt(seg.dataset.imgSegment));
          });
        }

        // Touch swipe navigation (mobile/tablet touch devices)
        const mediaWrap = card.querySelector('.card-media-wrap') || card.querySelector('.card-media');
        const supportsTouchSwipe = mediaWrap && window.matchMedia && (
          window.matchMedia('(hover: none)').matches ||
          window.matchMedia('(pointer: coarse)').matches
        );

        if (supportsTouchSwipe) {
          let startX = 0;
          let startY = 0;
          let deltaX = 0;
          let deltaY = 0;
          let isTouchActive = false;
          let axis = null;

          const SWIPE_THRESHOLD = 32;
          // Small on purpose. Safari decides which element a gesture scrolls
          // within the first few pixels, and once it has handed the gesture to
          // an ancestor scroller (the carousel track) a later preventDefault is
          // ignored. A slow swipe on a 120Hz screen only travels 2-3px per
          // frame, so anything larger here loses the race and the track scrolls
          // instead of the image changing.
          const INTENT_THRESHOLD = 4;

          const resetTouch = () => {
            isTouchActive = false;
            axis = null;
            deltaX = 0;
            deltaY = 0;
          };

          // Inside a carousel that restricts image swiping to the fully visible
          // card (data-active-card-swipe), a peeking card hands the gesture to
          // the track instead. The carousel keeps data-card-active current.
          const swipeBlocked = () => {
            const track = card.closest('[data-active-card-swipe]');
            return Boolean(track) && card.dataset.cardActive !== 'true';
          };

          mediaWrap.addEventListener('touchstart', (e) => {
            if (!e.touches || e.touches.length !== 1) return;
            if (swipeBlocked()) {
              isTouchActive = false;
              return;
            }
            const touch = e.touches[0];
            startX = touch.clientX;
            startY = touch.clientY;
            deltaX = 0;
            deltaY = 0;
            isTouchActive = true;
            axis = null;
          }, { passive: true });

          mediaWrap.addEventListener('touchmove', (e) => {
            if (!isTouchActive || !e.touches || e.touches.length !== 1) return;

            const touch = e.touches[0];
            deltaX = touch.clientX - startX;
            deltaY = touch.clientY - startY;
            const absX = Math.abs(deltaX);
            const absY = Math.abs(deltaY);

            // Commit to one axis for the whole gesture as soon as the finger
            // has moved at all, so a swipe can't be re-read as a scroll (or the
            // reverse) part-way through.
            if (!axis && Math.max(absX, absY) > INTENT_THRESHOLD) {
              axis = absX > absY ? 'x' : 'y';
            }

            // Horizontal belongs to the image gallery; vertical stays with the
            // page so the shopper can still scroll with a finger on a card.
            if (axis === 'x') {
              e.preventDefault();
            }
          }, { passive: false });

          mediaWrap.addEventListener('touchend', () => {
            if (!isTouchActive) return;
            isTouchActive = false;

            if (axis !== 'x' || Math.abs(deltaX) < SWIPE_THRESHOLD) return;

            const currentUrls = this._getCardImageUrls(card);
            const count = currentUrls.length;
            if (count <= 1) return;
            const current = parseInt(card.dataset.imgIndex || '0');
            const targetIndex = deltaX < 0
              ? (current + 1) % count
              : (current - 1 + count) % count;

            this._setCardImageIndex(card, targetIndex);
            card.dataset.suppressCardClick = 'true';
            setTimeout(() => {
              card.dataset.suppressCardClick = 'false';
            }, 250);
          }, { passive: true });

          // A cancelled gesture (e.g. the parent track takes over the scroll)
          // must not leave the handler primed to fire on the next touchend.
          mediaWrap.addEventListener('touchcancel', resetTouch, { passive: true });

          // Avoid opening the product page immediately after a swipe gesture.
          card.addEventListener('click', (e) => {
            if (card.dataset.suppressCardClick !== 'true') return;
            e.preventDefault();
            e.stopPropagation();
          }, true);
        }
      });
    }
  }

  /**
   * Convert a Nosto recommendation product into the shape the card renderer
   * expects (which follows the Nosto Search hit). Recommendation payloads
   * differ in three ways:
   *   - `categories` are collection titles, not ids, so promotion badges need
   *     the titles mapped back to offer-collection ids.
   *   - `skus` include sold-out variants, while Search returns only available
   *     ones. The CTA heuristic counts SKUs, so sold-out ones must go.
   *   - `url` is absolute; the card derives its handle from the last path
   *     segment, so it is rewritten to the canonical product path.
   * @param {Object} product - A product from a Nosto recommendation campaign
   * @returns {Object} Product in Search-hit shape
   */
  function fromRecommendation(product) {
    if (!product) return null;

    const handle = product.handle || product.url?.split('/').slice(-1)[0] || '';
    const titleMap = window.offerPromotionTitleMap || {};
    const categoryIds = (product.categories || [])
      .map((title) => titleMap[title])
      .filter(Boolean);

    const availableSkus = (product.skus || []).filter((sku) => sku.available !== false);

    return {
      ...product,
      productId: product.product_id || product.productId || '',
      url: handle ? `/products/${handle}` : (product.url || ''),
      handle,
      imageUrl: product.image_url || product.imageUrl || '',
      alternateImageUrls: product.alternate_image_urls || product.alternateImageUrls || [],
      listPrice: product.list_price ?? product.listPrice ?? '',
      customFields: product.custom_fields || product.customFields || {},
      categoryIds,
      skus: availableSkus
    };
  }

  /**
   * Back/forward cache restores the DOM as it was when the shopper left,
   * including an image src that a tap swapped in and never finished loading.
   * Safari then shows the broken-image icon. Put a card that never painted
   * its current src back on the primary image; refetch one that had painted.
   */
  function restoreCachedCardImage(card, index) {
    let urls = [];
    try {
      urls = JSON.parse(card.dataset.imgUrls || '[]');
    } catch (e) {
      return;
    }
    if (!urls.length || index < 0 || index >= urls.length) return;

    const img = card.querySelector('.card-media img');
    if (!img) return;

    card.dataset.imgIndex = String(index);
    img.loading = 'eager';
    img.dataset.painted = 'false';
    const url = urls[index];
    // Writing the same src again does not restart a failed request.
    img.removeAttribute('srcset');
    img.removeAttribute('src');
    img.srcset = IMAGE_WIDTHS.map((width) => `${withWidth(url, width)} ${width}w`).join(', ');
    img.src = withWidth(url, 540);

    card.querySelectorAll('[data-img-segment]').forEach((seg, i) => {
      seg.classList.toggle('active', i === index);
    });
  }

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;

    const recover = () => {
      document.querySelectorAll('.card--nosto[data-img-urls]').forEach((card) => {
        const img = card.querySelector('.card-media img');
        if (!img || img.naturalWidth > 0) return;

        const index = parseInt(card.dataset.imgIndex || '0', 10);
        const fallback = index > 0 && img.dataset.painted !== 'true' ? 0 : index;
        restoreCachedCardImage(card, fallback);
      });
    };

    // Give a restored image a frame to report its decoded size before
    // treating it as broken.
    requestAnimationFrame(() => {
      requestAnimationFrame(recover);
    });
  });

  /**
   * Give a rendered card its URL: `href` on the `data-card-link` links and
   * `data-url` on quick buy. The templates carry no URL placeholder, because
   * raw-HTML crawlers read `<template>` content and followed
   * `href="[[product.url]]"` as a relative link to a 404. Without a URL the
   * links stay without `href`, so they aren't broken links.
   * @param {ParentNode} root - Rendered card markup
   * @param {string} url - Card URL, or empty for none
   */
  function setCardUrl(root, url) {
    root.querySelectorAll('[data-card-link]').forEach((link) => {
      if (url) link.setAttribute('href', url);
      link.removeAttribute('data-card-link');
    });
    root.querySelectorAll('quick-buy').forEach((quickBuy) => {
      quickBuy.dataset.url = url;
    });
  }

  /**
   * Fill a rendered card's product id, handle and variant. The template
   * leaves these attributes empty; they're set here rather than by string
   * replace, so values never pass through the HTML parser. Attributes, not
   * properties: renderMarkup() returns the card as markup.
   * @param {ParentNode} root - Rendered card markup
   * @param {{ id: string, handle: string, variantId: (string|number|null) }} product
   */
  function setCardProduct(root, { id, handle, variantId }) {
    const productId = String(id || '');
    root.querySelector('[data-product-template]')?.setAttribute('data-product-id', productId);
    const quickBuy = root.querySelector('quick-buy');
    quickBuy?.setAttribute('data-product', productId);
    const quickBuyLink = quickBuy?.querySelector('.quick-buy_link');
    quickBuyLink?.setAttribute('data-product-handle', handle || '');
    quickBuyLink?.setAttribute('href', `#modal-${productId}-product`);
    root.querySelector('product-form form')?.setAttribute('id', `nosto-product-form-${productId}`);
    root.querySelector('product-form input[name="id"]')?.setAttribute('value', variantId || '');
    root.querySelectorAll('.yotpo-widget-instance').forEach((widget) => {
      widget.setAttribute('data-yotpo-product-id', productId);
      widget.setAttribute('data-yotpo-cart-product-id', productId);
    });
  }

  window.Woolman = window.Woolman || {};
  window.Woolman.NostoCard = {
    /**
     * Create a renderer bound to one host's options.
     * @param {Object} options
     * @returns {NostoCardRenderer}
     */
    create(options) {
      return new NostoCardRenderer(options);
    },
    fromRecommendation,
    isTouchOrSmallViewport,
    setCardUrl,
    syncTouchCtas: syncTouchCardCtas
  };
})();
